import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronUp, ShieldCheck, MessageSquare, Send, CheckCircle2, XCircle, Building2, Plane } from "lucide-react";
import api from "../lib/api";
import { Spinner, Pill } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ComplaintDetail() {
  const { cid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [comment, setComment] = useState("");
  const [respName, setRespName] = useState("");
  const [respBody, setRespBody] = useState("");

  const isMod = user && ["moderator", "admin"].includes(user.role);

  const load = useCallback(() => api.get(`/complaints/${cid}`).then((r) => setC(r.data)).catch(() => setErr(true)), [cid]);
  useEffect(() => { setC(null); setErr(false); load(); }, [cid, load]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">This complaint is not available.</div>;
  if (!c) return <Spinner full />;

  const upvote = async () => {
    if (!user) { toast.error("Sign in to upvote"); return; }
    const { data } = await api.post(`/complaints/${cid}/upvote`);
    setC({ ...c, upvote_count: data.upvote_count, has_upvoted: data.has_upvoted });
  };

  const submitComment = async () => {
    if (!user) { toast.error("Sign in to comment"); return; }
    if (comment.trim().length < 2) return;
    try {
      await api.post(`/complaints/${cid}/comments`, { body: comment });
      setComment(""); toast.success("Comment posted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const moderate = async (action) => {
    try { await api.post(`/complaints/${cid}/moderate`, { action }); toast.success(`Complaint ${action}ed`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitResponse = async () => {
    if (!respName.trim() || respBody.trim().length < 5) { toast.error("Enter responder name and message"); return; }
    try {
      await api.post(`/complaints/${cid}/official-response`, { responder_name: respName, body: respBody });
      setRespName(""); setRespBody(""); toast.success("Official response posted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const entityLink = c.entity_type === "airport" ? `/airports/${c.entity_iata || c.entity_id}` : `/airlines/${c.entity_id}`;

  return (
    <div data-testid="complaint-detail" className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
      <Link to="/complaints" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4"><ArrowLeft className="w-4 h-4" /> Forum</Link>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Pill tone="red">{c.category}</Pill>
        {c.status !== "published" && <Pill tone="amber">{c.status}</Pill>}
        {c.official_response && <Pill tone="green"><ShieldCheck className="w-3 h-3 mr-1 inline" />Official response</Pill>}
      </div>

      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight leading-tight">{c.title}</h1>

      <Link to={entityLink} className="inline-flex items-center gap-2 mt-4 border border-zinc-300 bg-white px-3 py-2 hover:border-primary transition-colors">
        {c.entity_type === "airport" ? <Building2 className="w-4 h-4 text-primary" /> : <Plane className="w-4 h-4 text-primary -rotate-45" />}
        <span className="text-sm font-medium">{c.entity_name}</span>
        {c.entity_iata && <span className="font-mono text-xs text-primary">{c.entity_iata}</span>}
      </Link>

      <div className="mt-6 flex gap-5">
        <button onClick={upvote} data-testid="detail-upvote"
          className={`flex flex-col items-center justify-center w-16 h-16 shrink-0 border transition-colors ${c.has_upvoted ? "border-primary bg-blue-50 text-primary" : "border-zinc-300 hover:border-primary hover:text-primary"}`}>
          <ChevronUp className="w-6 h-6" /><span className="font-mono font-500">{c.upvote_count}</span>
        </button>
        <div className="flex-1">
          <p className="text-zinc-800 leading-relaxed whitespace-pre-line">{c.body}</p>
          <div className="mt-3 text-xs font-mono text-muted-foreground">
            Filed by {c.author_name}{c.incident_date ? ` · incident ${c.incident_date}` : ""} · {new Date(c.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {c.official_response && (
        <div className="mt-8 border-l-4 border-emerald-500 bg-emerald-50/60 p-5" data-testid="official-response">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /><span className="font-heading font-700 text-sm">{c.official_response.responder_name}</span><Pill tone="green">Verified</Pill></div>
          <p className="text-sm text-zinc-800 whitespace-pre-line">{c.official_response.body}</p>
          <div className="mt-2 text-xs font-mono text-muted-foreground">{new Date(c.official_response.responded_at).toLocaleDateString()}</div>
        </div>
      )}

      {/* Moderator tools */}
      {isMod && (
        <div className="mt-8 border border-dashed border-primary/40 bg-blue-50/40 p-5" data-testid="moderator-tools">
          <div className="font-heading font-700 text-sm mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Moderator tools</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {c.status !== "published" && <button onClick={() => moderate("publish")} data-testid="mod-publish" className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white hover:bg-blue-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Publish</button>}
            <button onClick={() => moderate("resolve")} data-testid="mod-resolve" className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-emerald-500 text-emerald-700 hover:bg-emerald-50 transition-colors"><CheckCircle2 className="w-4 h-4" /> Mark resolved</button>
            <button onClick={() => moderate("reject")} data-testid="mod-reject" className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-destructive text-destructive hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
          </div>
          <div className="grid gap-2">
            <input value={respName} onChange={(e) => setRespName(e.target.value)} placeholder="Official responder name (e.g. Emirates Customer Relations)" data-testid="response-name"
              className="border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <textarea value={respBody} onChange={(e) => setRespBody(e.target.value)} placeholder="Post a verified official response…" rows={3} data-testid="response-body"
              className="border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <button onClick={submitResponse} data-testid="submit-response" className="self-start px-4 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">Post official response</button>
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="mt-10">
        <h2 className="font-heading font-700 text-xl mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Comments ({c.comments?.length || 0})</h2>
        <div className="space-y-3 mb-6">
          {c.comments?.map((cm) => (
            <div key={cm.comment_id} className="border border-zinc-300 bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-heading font-700 text-sm">{cm.author_name}</span>
                {["moderator", "admin"].includes(cm.role) && <Pill tone="blue">Moderator</Pill>}
                <span className="text-xs font-mono text-muted-foreground">{new Date(cm.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-zinc-800 whitespace-pre-line">{cm.body}</p>
            </div>
          ))}
          {(!c.comments || c.comments.length === 0) && <p className="text-sm text-muted-foreground">No comments yet.</p>}
        </div>
        {user ? (
          <div className="flex gap-2">
            <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitComment()}
              placeholder="Add a comment…" data-testid="comment-input"
              className="flex-1 border border-zinc-300 bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary" />
            <button onClick={submitComment} data-testid="comment-submit" className="px-4 bg-primary text-white hover:bg-blue-700 transition-colors"><Send className="w-4 h-4" /></button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground"><button onClick={() => nav("/login")} className="text-primary hover:underline">Sign in</button> to join the discussion.</p>
        )}
      </div>
    </div>
  );
}
